/**
 * Curated font catalog. Each entry maps to a Google Fonts family and a
 * full CSS font stack. Drivers:
 *   - `googleFontsUrl(keys)` returns a single href that loads everything
 *     selected, with `display=swap`.
 *   - `previewFontsUrl(keys)` returns a LIGHT (wght@400) href for picker
 *     previews — load on open so option labels render in their own face.
 *   - `fontFamilyForKey(key, role)` returns the CSS `font-family` value
 *     to write into a CSS variable.
 *
 * Catalog is curated (quality over quantity) but covers real range across
 * categories. Every entry MUST resolve to a real Google family (no silent
 * fallbacks). Adding a new entry is a one-liner.
 */

export type FontRole = "display" | "body" | "mono";
export type FontCategory = "serif" | "sans" | "mono" | "display" | "handwriting";

export type FontKey =
  // serif
  | "fraunces"
  | "playfair"
  | "cormorant"
  | "instrument-serif"
  | "dm-serif"
  | "lora"
  | "spectral"
  | "source-serif"
  | "libre-baskerville"
  | "newsreader"
  // sans
  | "dm-sans"
  | "inter"
  | "manrope"
  | "geist"
  | "ibm-plex-sans"
  | "work-sans"
  | "plus-jakarta"
  | "figtree"
  | "sora"
  | "outfit"
  // display
  | "space-grotesk"
  | "syne"
  | "bricolage"
  | "unbounded"
  | "archivo-black"
  | "anton"
  // mono
  | "jetbrains-mono"
  | "ibm-plex-mono"
  | "geist-mono"
  | "space-mono"
  | "fira-code"
  // handwriting
  | "caveat"
  | "kalam";

type FontEntry = {
  /** Display label shown in the picker. */
  label: string;
  /** Google Fonts family name as it appears in URLs. */
  google: string;
  /** Suffix after the family name in the CSS2 query, e.g. `:opsz,wght@9..144,400;9..144,600`. */
  axes: string;
  /** Full CSS font stack with quoted family + fallbacks. */
  stack: string;
  /** Roles this font is appropriate for (drives which picker it appears in). */
  roles: FontRole[];
  /** Visual category — groups the picker. */
  category: FontCategory;
  /** Short character note shown under the dropdown. */
  hint: string;
};

const SERIF_FB = "Georgia, 'Times New Roman', serif";
const SANS_FB = "ui-sans-serif, system-ui, sans-serif";
const MONO_FB = "ui-monospace, SFMono-Regular, monospace";

export const FONTS: Record<FontKey, FontEntry> = {
  /* ----------------------------- serif ----------------------------- */
  fraunces: {
    label: "Fraunces",
    google: "Fraunces",
    axes: ":opsz,wght@9..144,400;9..144,600;9..144,800",
    stack: `'Fraunces', ${SERIF_FB}`,
    roles: ["display"],
    category: "serif",
    hint: "Editorial, optical-size axis. Default.",
  },
  playfair: {
    label: "Playfair Display",
    google: "Playfair+Display",
    axes: ":wght@400;600;800",
    stack: `'Playfair Display', ${SERIF_FB}`,
    roles: ["display"],
    category: "serif",
    hint: "High-contrast classic serif.",
  },
  cormorant: {
    label: "Cormorant",
    google: "Cormorant",
    axes: ":wght@400;500;700",
    stack: `'Cormorant', ${SERIF_FB}`,
    roles: ["display"],
    category: "serif",
    hint: "Refined, tall x-height serif.",
  },
  "instrument-serif": {
    label: "Instrument Serif",
    google: "Instrument+Serif",
    axes: ":ital@0;1",
    stack: `'Instrument Serif', ${SERIF_FB}`,
    roles: ["display"],
    category: "serif",
    hint: "Modern editorial with elegant italic.",
  },
  "dm-serif": {
    label: "DM Serif Display",
    google: "DM+Serif+Display",
    axes: "",
    stack: `'DM Serif Display', ${SERIF_FB}`,
    roles: ["display"],
    category: "serif",
    hint: "Bold, condensed serif.",
  },
  lora: {
    label: "Lora",
    google: "Lora",
    axes: ":wght@400;500;600;700",
    stack: `'Lora', ${SERIF_FB}`,
    roles: ["display", "body"],
    category: "serif",
    hint: "Calligraphic, comfortable for reading.",
  },
  spectral: {
    label: "Spectral",
    google: "Spectral",
    axes: ":wght@400;500;700",
    stack: `'Spectral', ${SERIF_FB}`,
    roles: ["display", "body"],
    category: "serif",
    hint: "Screen-tuned serif with range.",
  },
  "source-serif": {
    label: "Source Serif 4",
    google: "Source+Serif+4",
    axes: ":opsz,wght@8..60,400;8..60,600;8..60,700",
    stack: `'Source Serif 4', ${SERIF_FB}`,
    roles: ["display", "body"],
    category: "serif",
    hint: "Workhorse serif, optical sizes.",
  },
  "libre-baskerville": {
    label: "Libre Baskerville",
    google: "Libre+Baskerville",
    axes: ":wght@400;700",
    stack: `'Libre Baskerville', ${SERIF_FB}`,
    roles: ["display"],
    category: "serif",
    hint: "Traditional book serif.",
  },
  newsreader: {
    label: "Newsreader",
    google: "Newsreader",
    axes: ":opsz,wght@6..72,400;6..72,500;6..72,600",
    stack: `'Newsreader', ${SERIF_FB}`,
    roles: ["display", "body"],
    category: "serif",
    hint: "Literary, news-grade serif.",
  },

  /* ------------------------------ sans ----------------------------- */
  "dm-sans": {
    label: "DM Sans",
    google: "DM+Sans",
    axes: ":wght@400;500;700",
    stack: `'DM Sans', ${SANS_FB}`,
    roles: ["body"],
    category: "sans",
    hint: "Geometric sans, tuned for body. Default.",
  },
  inter: {
    label: "Inter",
    google: "Inter",
    axes: ":wght@400;500;600;700",
    stack: `'Inter', ${SANS_FB}`,
    roles: ["body"],
    category: "sans",
    hint: "Industry-standard UI sans.",
  },
  manrope: {
    label: "Manrope",
    google: "Manrope",
    axes: ":wght@400;500;700",
    stack: `'Manrope', ${SANS_FB}`,
    roles: ["body"],
    category: "sans",
    hint: "Modern open sans, friendly.",
  },
  geist: {
    label: "Geist",
    google: "Geist",
    axes: ":wght@400;500;700",
    stack: `'Geist', ${SANS_FB}`,
    roles: ["body"],
    category: "sans",
    hint: "Vercel's geometric sans.",
  },
  "ibm-plex-sans": {
    label: "IBM Plex Sans",
    google: "IBM+Plex+Sans",
    axes: ":wght@400;500;700",
    stack: `'IBM Plex Sans', ${SANS_FB}`,
    roles: ["body"],
    category: "sans",
    hint: "Corporate grotesque with character.",
  },
  "work-sans": {
    label: "Work Sans",
    google: "Work+Sans",
    axes: ":wght@400;500;600",
    stack: `'Work Sans', ${SANS_FB}`,
    roles: ["body"],
    category: "sans",
    hint: "Versatile grotesque for UI + body.",
  },
  "plus-jakarta": {
    label: "Plus Jakarta Sans",
    google: "Plus+Jakarta+Sans",
    axes: ":wght@400;500;700",
    stack: `'Plus Jakarta Sans', ${SANS_FB}`,
    roles: ["body"],
    category: "sans",
    hint: "Crisp, contemporary sans.",
  },
  figtree: {
    label: "Figtree",
    google: "Figtree",
    axes: ":wght@400;500;600",
    stack: `'Figtree', ${SANS_FB}`,
    roles: ["body"],
    category: "sans",
    hint: "Soft, rounded product sans.",
  },
  sora: {
    label: "Sora",
    google: "Sora",
    axes: ":wght@400;500;700",
    stack: `'Sora', ${SANS_FB}`,
    roles: ["display", "body"],
    category: "sans",
    hint: "Techy geometric, good for headings.",
  },
  outfit: {
    label: "Outfit",
    google: "Outfit",
    axes: ":wght@400;500;700",
    stack: `'Outfit', ${SANS_FB}`,
    roles: ["display", "body"],
    category: "sans",
    hint: "Clean uniform-width sans.",
  },

  /* ----------------------------- display --------------------------- */
  "space-grotesk": {
    label: "Space Grotesk",
    google: "Space+Grotesk",
    axes: ":wght@400;500;700",
    stack: `'Space Grotesk', ${SANS_FB}`,
    roles: ["display", "body"],
    category: "display",
    hint: "Quirky grotesque, strong headlines.",
  },
  syne: {
    label: "Syne",
    google: "Syne",
    axes: ":wght@400;600;800",
    stack: `'Syne', ${SANS_FB}`,
    roles: ["display"],
    category: "display",
    hint: "Art-y, extended display sans.",
  },
  bricolage: {
    label: "Bricolage Grotesque",
    google: "Bricolage+Grotesque",
    axes: ":opsz,wght@12..96,400;12..96,600;12..96,800",
    stack: `'Bricolage Grotesque', ${SANS_FB}`,
    roles: ["display"],
    category: "display",
    hint: "Characterful editorial display.",
  },
  unbounded: {
    label: "Unbounded",
    google: "Unbounded",
    axes: ":wght@400;600;800",
    stack: `'Unbounded', ${SANS_FB}`,
    roles: ["display"],
    category: "display",
    hint: "Round, bold, poster-grade.",
  },
  "archivo-black": {
    label: "Archivo Black",
    google: "Archivo+Black",
    axes: "",
    stack: `'Archivo Black', ${SANS_FB}`,
    roles: ["display"],
    category: "display",
    hint: "Heavy, grotesque, impactful.",
  },
  anton: {
    label: "Anton",
    google: "Anton",
    axes: "",
    stack: `'Anton', ${SANS_FB}`,
    roles: ["display"],
    category: "display",
    hint: "Tall condensed poster face.",
  },

  /* ------------------------------ mono ----------------------------- */
  "jetbrains-mono": {
    label: "JetBrains Mono",
    google: "JetBrains+Mono",
    axes: ":wght@400;500",
    stack: `'JetBrains Mono', ${MONO_FB}`,
    roles: ["mono"],
    category: "mono",
    hint: "Code-grade mono. Default.",
  },
  "ibm-plex-mono": {
    label: "IBM Plex Mono",
    google: "IBM+Plex+Mono",
    axes: ":wght@400;500",
    stack: `'IBM Plex Mono', ${MONO_FB}`,
    roles: ["mono"],
    category: "mono",
    hint: "Slabbed mono with personality.",
  },
  "geist-mono": {
    label: "Geist Mono",
    google: "Geist+Mono",
    axes: ":wght@400;500",
    stack: `'Geist Mono', ${MONO_FB}`,
    roles: ["mono"],
    category: "mono",
    hint: "Pairs with Geist sans.",
  },
  "space-mono": {
    label: "Space Mono",
    google: "Space+Mono",
    axes: ":wght@400;700",
    stack: `'Space Mono', ${MONO_FB}`,
    roles: ["mono"],
    category: "mono",
    hint: "Retro-futuristic mono.",
  },
  "fira-code": {
    label: "Fira Code",
    google: "Fira+Code",
    axes: ":wght@400;500",
    stack: `'Fira Code', ${MONO_FB}`,
    roles: ["mono"],
    category: "mono",
    hint: "Ligature-rich coding mono.",
  },

  /* --------------------------- handwriting ------------------------- */
  caveat: {
    label: "Caveat",
    google: "Caveat",
    axes: ":wght@400;600;700",
    stack: `'Caveat', ui-serif, cursive`,
    roles: ["display"],
    category: "handwriting",
    hint: "Friendly handwritten marker.",
  },
  kalam: {
    label: "Kalam",
    google: "Kalam",
    axes: ":wght@400;700",
    stack: `'Kalam', ui-serif, cursive`,
    roles: ["display"],
    category: "handwriting",
    hint: "Casual handwriting, warm.",
  },
};

/** Human label per category, in display order. */
export const FONT_CATEGORY_LABEL: Record<FontCategory, string> = {
  serif: "Serif",
  sans: "Sans",
  display: "Display",
  mono: "Mono",
  handwriting: "Handwriting",
};
const CATEGORY_ORDER: FontCategory[] = ["serif", "sans", "display", "mono", "handwriting"];

/** Keys filtered to a given role, in insertion order. */
export function fontsForRole(role: FontRole): { key: FontKey; entry: FontEntry }[] {
  return Object.entries(FONTS)
    .filter(([, e]) => e.roles.includes(role))
    .map(([k, entry]) => ({ key: k as FontKey, entry }));
}

/** Role fonts grouped by category (category order fixed), for the picker. */
export function fontsForRoleByCategory(
  role: FontRole,
): { category: FontCategory; items: { key: FontKey; entry: FontEntry }[] }[] {
  const opts = fontsForRole(role);
  return CATEGORY_ORDER.map((category) => ({
    category,
    items: opts.filter((o) => o.entry.category === category),
  })).filter((g) => g.items.length > 0);
}

/** CSS `font-family` value for a key, with role-appropriate fallback. */
export function fontFamilyForKey(key: FontKey | undefined, role: FontRole): string {
  const entry = key ? FONTS[key] : undefined;
  if (entry) return entry.stack;
  // Sensible defaults if the key is unknown / undefined.
  if (role === "display") return SERIF_FB;
  if (role === "mono") return MONO_FB;
  return SANS_FB;
}

/**
 * One Google Fonts CSS2 URL covering every selected family. Duplicates
 * (e.g. same key picked twice) are collapsed. Skips keys not in the
 * catalog. Always appends `display=swap` so page paint is non-blocking.
 */
export function googleFontsUrl(keys: (FontKey | undefined)[]): string {
  const seen = new Set<string>();
  const families: string[] = [];
  for (const k of keys) {
    if (!k) continue;
    const e = FONTS[k];
    if (!e) continue;
    if (seen.has(e.google)) continue;
    seen.add(e.google);
    families.push(`family=${e.google}${e.axes}`);
  }
  if (families.length === 0) return "https://fonts.googleapis.com/css2?display=swap";
  return `https://fonts.googleapis.com/css2?${families.join("&")}&display=swap`;
}

/**
 * A LIGHT URL for picker previews: loads each family at a single weight (400)
 * so option labels render in-face cheaply. Loaded lazily when a picker opens.
 */
export function previewFontsUrl(keys: (FontKey | undefined)[]): string {
  const seen = new Set<string>();
  const families: string[] = [];
  for (const k of keys) {
    if (!k) continue;
    const e = FONTS[k];
    if (!e) continue;
    if (seen.has(e.google)) continue;
    seen.add(e.google);
    families.push(`family=${e.google}:wght@400`);
  }
  if (families.length === 0) return "https://fonts.googleapis.com/css2?display=swap";
  return `https://fonts.googleapis.com/css2?${families.join("&")}&display=swap`;
}
